import { useMemo } from 'react';
import { toastr } from 'react-redux-toastr';
import { CERTIFICATION_TYPE_DISPLAY_NAMES_LOWERCASE_KEY_MAP } from '../../constants/affordableCertificationTypes';
import { COMPLIANCE_DOCUMENT_TYPES_SHORT_TO_KEY_MAP } from '../../constants/complianceDocumentTypes';
import { PROGRAM_NAMES_KEY_MAP } from '../../constants/affordableProgramNames';
import generalMessages from '../App/messages';
import { COMPLIANCE_TYPES } from './constants';
import componentMessages from './messages';
import useDownloadComplianceOverviewReportMutation from './hooks.useDownloadComplianceOverviewReportMutation';

const messages = { ...generalMessages, ...componentMessages };

const YES_NO = {
  yes: 'Yes',
  no: 'No',
};

const VOUCHER_EFFECTIVE_DATE_STATUS = {
  pastDue: 'pastDue',
  attentionRequired: 'attentionRequired',
};

// Values the lookup can't resolve go out verbatim: the backend matches with
// exact equality, so re-casing a value it never emitted can only miss.
const getItemKey = (item: string) => {
  return (
    CERTIFICATION_TYPE_DISPLAY_NAMES_LOWERCASE_KEY_MAP[item] ??
    COMPLIANCE_DOCUMENT_TYPES_SHORT_TO_KEY_MAP[item] ??
    PROGRAM_NAMES_KEY_MAP[item] ??
    YES_NO[item] ??
    VOUCHER_EFFECTIVE_DATE_STATUS[item] ??
    item
  );
};

const FILTER_LOOKUP_FIELDS = [
  'certificationType',
  'complianceDocumentType',
  'propertyName',
  'programType',
  'complianceApprovalStatus',
  'certStatus',
];

type ReverseLookup = { [lowercased: string]: string };
type FilterOptionsByField = { [field: string]: ?Array<string> };
type ResultRow = { [field: string]: mixed };

// Dropdown values are lowercased by the table library, so map them back to
// the exact spelling the backend emitted.
export const buildReverseLookup = (
  results: Array<ResultRow>,
): ReverseLookup => {
  const lookup = {};

  results.forEach((result) => {
    FILTER_LOOKUP_FIELDS.forEach((field) => {
      const val = result[field];
      if (val != null) {
        lookup[String(val).toLowerCase()] = String(val);
      }
    });
  });

  return lookup;
};

// The filter-options endpoint covers the whole population, not just the
// loaded page, so a persisted filter resolves even before any row is loaded.
export const buildReverseLookupFromOptions = (
  filterOptions: ?FilterOptionsByField,
): ReverseLookup => {
  const lookup = {};

  FILTER_LOOKUP_FIELDS.forEach((field) => {
    (filterOptions?.[field] ?? []).forEach((val) => {
      lookup[String(val).toLowerCase()] = String(val);
    });
  });

  return lookup;
};

const mapYesNoToStatusFilter = (
  value: string[] | string,
  yesKey: string,
  noKey: string,
) => {
  const values = Array.isArray(value) ? value : [value];
  const mapped = values.reduce((acc, item) => {
    if (item === 'yes') {
      acc.push(yesKey);
    }
    if (item === 'no') {
      acc.push(noKey);
    }
    return acc;
  }, []);

  if (!mapped.length) {
    return null;
  }

  return Object.fromEntries(mapped.map((item) => [item, true]));
};
type DateBound = { format: (pattern: string) => string };
type DateRangeState = {
  [id: string]: { fromDate: ?DateBound, toDate: ?DateBound },
};

const toCalendarDate = (date: ?DateBound): string | null =>
  date ? date.format('YYYY-MM-DD') : null;

// The library's client-side date filter compared calendar days (inclusive),
// so only the picked Y-M-D travels to the backend, never a timestamp.
const adaptDateRanges = (dateState: ?DateRangeState) =>
  Object.entries(dateState ?? {})
    .filter(([, range]) => range && (range.fromDate || range.toDate))
    .map(([id, range]) => [
      id,
      {
        from: toCalendarDate(range.fromDate),
        to: toCalendarDate(range.toDate),
      },
    ]);

// Shared with hooksV2.js so the table's server-side query and the CSV/PDF
// export send identical filter/sort/search params for the same table state.
export const adaptFiltersForBackend = ({
  dateState,
  filterState,
  order,
  orderBy,
  searchState,
  includeNotStartedCerts,
  reverseLookup = {},
}: {
  dateState?: ?DateRangeState,
  filterState: { [id: string]: string[] | string },
  searchState: { [id: string]: string },
  reverseLookup?: ReverseLookup,
}) => {
  const { certStatus, ...otherFilters } = filterState;
  const activeFilterState = includeNotStartedCerts ? filterState : otherFilters;
  // Dynamically Map filterState
  const mappedFilterState = Object.entries(activeFilterState)
    .map(([key, value]) => {
      // Handle derived boolean filters
      if (key === 'isPastDue') {
        const filter = mapYesNoToStatusFilter(value, 'pastDue', 'notPastDue');
        return filter ? [key, filter] : null;
      }

      if (key === 'isAttentionRequired') {
        const filter = mapYesNoToStatusFilter(
          value,
          'attentionRequired',
          'notAttentionRequired',
        );
        return filter ? [key, filter] : null;
      }

      if (key === 'isReviewOverdue') {
        const filter = mapYesNoToStatusFilter(
          value,
          'reviewOverdue',
          'notReviewOverdue',
        );
        return filter ? [key, filter] : null;
      }

      // recertRange (ENG-30055 Phase 3a planning-range quick filter) carries
      // raw bucket-id strings (e.g. 'late', 'under30', 'between31and60') that
      // fortress-backend's export filter-strategy map expects verbatim — do
      // NOT run these through getItemKey's generic title-casing fallback like
      // every other array filter below, or the backend strategy lookup won't
      // match (it'd receive 'Late' instead of 'late').
      if (key === 'recertRange') {
        const values = Array.isArray(value) ? value : [value];
        if (!values.length) {
          return null;
        }
        const filterObject = Object.fromEntries(
          values.map((item) => [item, true]),
        );
        return [key, filterObject];
      }

      let filter = null;

      if (Array.isArray(value)) {
        filter = value.map((item) => {
          // For yes/no boolean filters, use the YES_NO map
          if (YES_NO[item]) {
            return [YES_NO[item], true];
          }
          // For all other filters, use reverse lookup first, fall back to getItemKey
          const mappedValue = reverseLookup[item] ?? getItemKey(item);
          return [mappedValue, true];
        });
      } else {
        // Legacy string support for backward compatibility
        const mappedValue =
          YES_NO[value] ?? reverseLookup[value] ?? getItemKey(value);
        filter = [[mappedValue, true]];
      }

      const filterObject = Object.fromEntries(filter);

      return [key, filterObject];
    })
    .filter((entry) => entry !== null); // Remove null entries from isPastDue handling

  const transformedFilterState = Object.fromEntries([
    ...mappedFilterState,
    ...adaptDateRanges(dateState),
  ]);

  return {
    currentFilter: transformedFilterState,
    currentSorting: { fieldName: orderBy, order },
    // turn searchState into a string
    searchText: Object.keys(searchState)
      .map((key) => `${key}:${searchState[key]}`)
      .join(', '),
  };
};

const useDownloadCSVPDF = ({
  complianceType,
  dateState,
  filterState,
  intl,
  order,
  orderBy,
  organizationId,
  searchState,
  includeNotStartedCerts,
  results = [],
  serverFilterOptions,
}) => {
  const { currentFilter, currentSorting, searchText } = useMemo(() => {
    const reverseLookup = {
      ...buildReverseLookup(results),
      ...buildReverseLookupFromOptions(serverFilterOptions),
    };
    return adaptFiltersForBackend({
      // Only All Open Certs filters server-side; Needs Review still filters dates client-side.
      dateState:
        complianceType === COMPLIANCE_TYPES.ALL_OPEN_CERTS ? dateState : null,
      filterState,
      order: order.toUpperCase(),
      orderBy,
      searchState,
      includeNotStartedCerts,
      reverseLookup,
    });
  }, [
    complianceType,
    dateState,
    filterState,
    order,
    orderBy,
    searchState,
    includeNotStartedCerts,
    results,
    serverFilterOptions,
  ]);

  const { isLoading: isCSVPDFExportLoading, mutate: handleDownloadCSVPDF } =
    useDownloadComplianceOverviewReportMutation({
      organizationId,
      complianceType,
      includeNotStartedCerts,
      fileType: 'csv',
      searchText,
      currentFilter,
      currentSorting,
      intl,
      options: {
        onError: () => {
          toastr.error(
            intl.formatMessage(messages.errorDownloading),
            intl.formatMessage(messages.failedDocumentDownload),
          );
        },
      },
    });

  return { handleDownloadCSVPDF, isCSVPDFExportLoading };
};

export default useDownloadCSVPDF;
